<template>
  <a-sticky class="contact-widget__sticky">
    <ul class="contact-widget__list">
      <li
        class="contact-widget__list__item"
        :class="dynamicNewsletterItemClass"
      >
        <button
          v-if="displayWidgetButtons"
          :id="$options.consts.ACTIVATOR_ID.NEWSLETTER_WIDGET_BUTTON"
          aria-label="Newsletter"
          class="contact-widget__list__item__button"
          :data-ref="$options.consts.REFS.NEWSLETTER_BUTTON"
          :disabled="showContactUs"
          v-observe-visibility="setNewsletterButtonVisibility"
          @click="onShowNewsletterModal"
        >
          <a-icon
            :icon="$options.consts.ICON.NEWSLETTER_RECT_BLUE"
            :color="$options.consts.ICON_COLOR.BLUE"
            :disabled="showContactUs"
          />
        </button>
        <label
          for="contact-widget__newsletter-button"
          class="contact-widget__list__item__button__label"
        >
          Newsletter
        </label>
        <div v-if="isLazyLoaded">
          <a-modal-widget
            v-model="showNewsletter"
            :activator-selector="newsletterActivatorSelector"
            :show-social-networks="displayWidgetButtons"
            :parent-classes-to-prevent-clickaway="
              $options.consts.CLASSES_TO_PREVENT_WIDGET_CLOSE_ON_BLUR
            "
            success-message-type="newsletter"
            :scrollTop="-145"
            :slot-selector="$options.consts.SELECTOR.NEWSLETTER_FORM"
            :error-message-on-slot-load-failure="
              $options.consts.FORM_LOAD_ERROR_MESSAGE.NEWSLETTER
            "
            @scroll-end="focusNewsletterFormInput"
            @close-modal="resetActivators"
          >
            <a-newsletter-form
              ref="newsletterForm"
              class="contact-widget__form"
              slot-scope="{ closeModal }"
              :close-on-submit="closeModal"
              :form-id="$options.consts.FORM_ID.NEWSLETTER_WIDGET"
            />
          </a-modal-widget>
        </div>
      </li>
      <li
        class="contact-widget__list__item"
        :data-ref="$options.consts.REFS.CONTACT_WIDGET"
      >
        <button
          v-if="displayWidgetButtons"
          :id="$options.consts.ACTIVATOR_ID.CONTACT_US_WIDGET_BUTTON"
          aria-label="Contact Us"
          :disabled="showNewsletter"
          class="contact-widget__list__item__button"
          @click="onShowContactUsModal"
        >
          <a-icon
            :icon="$options.consts.ICON.ENVELOPE_RECT_BLUE"
            :color="$options.consts.ICON_COLOR.BLUE"
            :disabled="showNewsletter"
          />
        </button>
        <label
          for="contact-widget__contact-us-button"
          class="contact-widget__list__item__button__label"
        >
          Contact us
        </label>
        <div v-if="isLazyLoaded">
          <a-modal-widget
            v-model="showContactUs"
            :show-social-networks="displayWidgetButtons"
            :activator-selector="contactUsActivatorSelector"
            :parent-classes-to-prevent-clickaway="
              $options.consts.CLASSES_TO_PREVENT_WIDGET_CLOSE_ON_BLUR
            "
            success-message-type="contact-us"
            :links-top="89"
            :scrollTop="-195"
            :slot-selector="$options.consts.SELECTOR.CONTACT_US_FORM"
            :error-message-on-slot-load-failure="
              $options.consts.FORM_LOAD_ERROR_MESSAGE.CONTACT_US
            "
            @scroll-end="focusContactUsInput"
            @close-modal="resetActivators"
          >
            <a-contact-us-form
              ref="contactUsForm"
              class="contact-widget__form"
              slot-scope="{ closeModal }"
              :closeOnSubmit="closeModal"
              :form-id="$options.consts.FORM_ID.CONTACT_US_WIDGET"
            />
          </a-modal-widget>
        </div>
      </li>
      <li
        v-if="showContactUs || showNewsletter"
        class="contact-widget__list__item socials"
      >
        <a-icon-list
          :icons="icons"
          :layout="$options.consts.LAYOUT.VERTICAL"
          :icon-color="$options.consts.ICON_COLOR.CUSTOM"
        />
      </li>
    </ul>
  </a-sticky>
</template>

<script>
import { mapGetters } from 'vuex'

import AIcon, { ICON, ICON_COLOR } from 'shared/AIcon'
import { PROP_TYPES, propValidator } from '@/utils/validators'
import { REFS } from '@/utils/enums/external-refs'
import { FORM_ID } from 'enums/form-id'
import ASticky from 'shared/ASticky'
import AIconList, { LAYOUT } from 'shared/AIconList'
import { WIDGETS_WRAPPER_CLASS } from '@/components/_layout/ALayoutWidgets/enums'
import {
  OPEN_LAYOUT_WIDGET_BUS_NAME,
  CLOSE_LAYOUT_WIDGETS_BUS_NAME
} from 'shared/AContactWidget/enums'
import {
  ACTIVATOR_SELECTOR_BY_ACTIVATOR_ID,
  ACTIVATOR_ID
} from 'enums/activator-id'

export const WIDGET = {
  CONTACT_US: 'contact-us',
  NEWSLETTER: 'newsletter'
}

const IS_NEWSLETTER_FORM_SHOWN_STORAGE_KEY = 'isNewsletterFormShown'

export default {
  name: 'AContactWidget',
  components: {
    AIconList,
    AModalWidget: () => import('modals/AModalWidget'),
    AContactUsForm: () => import('shared/AContactUsForm'),
    ANewsletterForm: () => import('shared/ANewsletterForm'),
    AIcon,
    ASticky
  },
  props: {
    displayWidgetButtons: propValidator([PROP_TYPES.BOOLEAN], false, true)
  },
  consts: {
    FORM_ID,
    REFS,
    ICON,
    ICON_COLOR,
    LAYOUT,
    SELECTOR: {
      NEWSLETTER_FORM: `.contact-widget__list #${FORM_ID.NEWSLETTER_WIDGET}`,
      CONTACT_US_FORM: `.contact-widget__list #${FORM_ID.CONTACT_US_WIDGET}`,
      NEWSLETTER_BUTTON:
        ACTIVATOR_SELECTOR_BY_ACTIVATOR_ID[
          ACTIVATOR_ID.NEWSLETTER_WIDGET_BUTTON
        ],
      CONTACT_US_BUTTON:
        ACTIVATOR_SELECTOR_BY_ACTIVATOR_ID[
          ACTIVATOR_ID.CONTACT_US_WIDGET_BUTTON
        ]
    },
    FORM_LOAD_ERROR_MESSAGE: {
      NEWSLETTER: 'Error loading a Newsletter Form. Please try to reopen it.',
      CONTACT_US: 'Error loading a Contact Us Form. Please try to reopen it.'
    },
    CLASSES_TO_PREVENT_WIDGET_CLOSE_ON_BLUR: [WIDGETS_WRAPPER_CLASS],
    ACTIVATOR_ID
  },
  data() {
    return {
      contactUsActivatorSelector: this.$options.consts.SELECTOR
        .CONTACT_US_BUTTON,
      newsletterActivatorSelector: this.$options.consts.SELECTOR
        .NEWSLETTER_BUTTON,
      isLazyLoaded: false,
      showContactUs: false,
      showNewsletter: false,
      isNewsletterButtonVisible: false,
      openWidgetHandlers: {
        [WIDGET.CONTACT_US]: this.onShowContactUsModal,
        [WIDGET.NEWSLETTER]: this.onShowNewsletterModal
      },
      REFS,
      icons: [
        {
          url: this.$env.SOCIAL_LINK_FACEBOOK_FM,
          icon: ICON.SOCIAL_SQUARE_FACEBOOK,
          openInNewTab: true,
          original: true
        },
        {
          url: this.$env.SOCIAL_LINK_LINKEDIN_FM,
          icon: ICON.SOCIAL_SQUARE_LINKEDIN,
          openInNewTab: true,
          original: true
        },
        {
          url: this.$env.SOCIAL_LINK_TELEGRAM_FM,
          icon: ICON.SOCIAL_SQUARE_TELEGRAM,
          openInNewTab: true,
          original: true
        },
        {
          url: this.$env.SOCIAL_LINK_TWITTER_FM,
          icon: ICON.SOCIAL_SQUARE_X,
          openInNewTab: true,
          original: true
        },
        /**
         * ToDo: Waiting for the square icon for the YouTube
         */
        {
          url: this.$env.SOCIAL_LINK_YOUTUBE_FM,
          icon: ICON.YOUTUBE_ROUND_BLACK,
          openInNewTab: true,
          original: true
        }
      ]
    }
  },
  computed: {
    ...mapGetters({
      isLoggedIn: 'auth/isLoggedIn',
      userDetails: 'auth/userDetails',
      isInterstitialBannerVisible: 'isInterstitialBannerVisible'
    }),
    dynamicNewsletterItemClass() {
      const isNewsletterFormAlreadyShown = !!localStorage.getItem(
        IS_NEWSLETTER_FORM_SHOWN_STORAGE_KEY
      )

      /** Newsletter button is animated if:
       *     Newsletter button is visible
       *  && Newsletter form is hidden
       *  && Newsletter form have been never opened at all
       *  && User is logged out OR if user is logged in and not subscribed to newsletter **/
      return {
        'contact-widget__list__item-animated':
          this.isNewsletterButtonVisible &&
          !this.showNewsletter &&
          !isNewsletterFormAlreadyShown &&
          !this.isInterstitialBannerVisible &&
          (!this.isLoggedIn || !this.userDetails.HasFmNewsletterSubscriptions)
      }
    }
  },
  methods: {
    resetActivators() {
      const {
        CONTACT_US_BUTTON,
        NEWSLETTER_BUTTON
      } = this.$options.consts.SELECTOR

      this.contactUsActivatorSelector = CONTACT_US_BUTTON
      this.newsletterActivatorSelector = NEWSLETTER_BUTTON
    },
    closeWidgets() {
      this.showContactUs = false
      this.showNewsletter = false
    },
    onShowContactUsModal({
      activatorSelector = this.contactUsActivatorSelector
    } = {}) {
      this.closeWidgets()
      this.showContactUs = true
      this.isLazyLoaded = true
      this.contactUsActivatorSelector = activatorSelector
    },
    onShowNewsletterModal({
      activatorSelector = this.newsletterActivatorSelector
    } = {}) {
      this.closeWidgets()
      this.showNewsletter = true
      this.isLazyLoaded = true
      localStorage.setItem(IS_NEWSLETTER_FORM_SHOWN_STORAGE_KEY, true)
      this.newsletterActivatorSelector = activatorSelector
    },
    setNewsletterButtonVisibility(isVisible) {
      this.isNewsletterButtonVisible = isVisible
    },
    focusContactUsInput() {
      if (!this.$refs.contactUsForm) return

      this.$refs.contactUsForm.focusInput()
    },
    focusNewsletterFormInput() {
      if (!this.$refs.newsletterForm) return

      this.$refs.newsletterForm.focusInput()
    }
  },
  mounted() {
    this.$bus.$on(OPEN_LAYOUT_WIDGET_BUS_NAME, ({ name, activatorSelector }) =>
      this.openWidgetHandlers[name]({ activatorSelector })
    )

    this.$bus.$on(CLOSE_LAYOUT_WIDGETS_BUS_NAME, this.closeWidgets)
  },
  beforeDestroy() {
    this.$bus.$off(OPEN_LAYOUT_WIDGET_BUS_NAME)
    this.$bus.$off(CLOSE_LAYOUT_WIDGETS_BUS_NAME)
  }
}
</script>

<style lang="scss" scoped>
.contact-widget__sticky {
  top: 145px;
  width: 35px;
  pointer-events: all;
  margin-bottom: 54px;

  .contact-widget__list {
    margin: 0;
  }

  .contact-widget__list__item {
    position: relative;
    list-style-type: none;
    font-size: 0;

    &.socials {
      margin-top: 10px;
    }
  }

  .contact-widget__list__item-animated {
    animation-duration: 7s;
    animation-name: pulse;
    animation-iteration-count: Infinite;
    animation-timing-function: ease-out;
    animation-delay: 0.5s;
  }

  @keyframes pulse {
    0%,
    14%,
    28%,
    42%,
    100% {
      transform: scale3d(1, 1, 1);
    }
    7%,
    21%,
    35% {
      transform: scale3d(1.05, 1.05, 1.05) translateX(-5px);
    }
  }

  .contact-widget__list__item__button__label {
    position: absolute;
    right: 100%;
    top: 50%;
    transform: translate(50%, -50%);
    opacity: 0;

    font-size: 13px;
    line-height: 1.3;
    padding: 1px 5px;
    color: #626262;
    background: white;
    border: 1px solid;
    border-right: none;
    word-break: initial;
    white-space: nowrap;
    transition: transform 0.2s, opacity 0.2s;
    pointer-events: none;
  }

  .contact-widget__form-loading-spinner {
    margin: 35px 0;
  }

  .contact-widget__form {
    padding: 38px 37px 30px 32px;

    @include mobile {
      padding: 44px 20px 15px;
    }
  }

  .contact-widget__list__item__button {
    position: relative;
    z-index: 1;
    background: white;
    font-size: 0;
    margin-bottom: 15px;

    & * {
      cursor: pointer;
    }

    &:disabled * {
      cursor: auto;
    }

    &:hover {
      .widget-button__icon {
        fill: black;
      }

      ~ .contact-widget__list__item__button__label {
        transform: translate(0, -50%);
        opacity: 1;
      }
    }
  }
}
</style>
<style lang="scss">
#__nuxt {
  .global-wrapper.preview-mode .contact-widget__sticky {
    top: calc(145px + #{$size-preview-panel});
  }
}
</style>
